/**
 * React wrapper around the imperative mall scene.
 *
 * The scene is created ONCE and lives for the life of the component; every
 * state change is pushed in through a setter effect. Re-creating it on prop
 * change would rebuild ~70 meshes and drop the camera, which is exactly the
 * jank the imperative split exists to avoid.
 */

import React, { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { createMallScene } from './mallScene';
import { CATEGORY_COLORS } from '../data/mallData';
import { formatMoney, formatNumber } from '../../Shared/Formatters';

const MallCanvas = forwardRef(function MallCanvas({
    floor,
    selectedId,
    visibleUnitIds,
    heatmap = 'none',
    stackMode = false,
    labels = true,
    walkable = null,
    showWalkable = false,
    route = null,
    theme = 'dark',
    units,
    showFurniture = true,
    planSize,
    amenities,
    surfaces,
    onSelect,
}, ref) {
    const containerRef = useRef(null);
    const apiRef = useRef(null);
    const onSelectRef = useRef(onSelect);
    onSelectRef.current = onSelect;

    const [hover, setHover] = useState(null); // { unit, x, y }
    const [ready, setReady] = useState(false);

    const unitsRef = useRef(units);
    unitsRef.current = units;

    const handleHover = useCallback((unitId, pos) => {
        if (!unitId || !pos) { setHover(null); return; }
        const list = unitsRef.current || [];
        const unit = list.find((u) => u.id === unitId);
        if (!unit) { setHover(null); return; }
        setHover({ unit, x: pos.x, y: pos.y });
    }, []);

    // Create once.
    useEffect(() => {
        if (!containerRef.current) return undefined;
        // Created once. The unit set is a construction parameter, so the PAGE
        // remounts this component (via key) when the data source changes rather
        // than trying to swap 400+ meshes in place.
        const api = createMallScene(containerRef.current, {
            onSelect: (id) => onSelectRef.current && onSelectRef.current(id),
            onHover: handleHover,
            theme,
            units,
            showFurniture,
            ...(planSize ? { planSize } : {}),
            ...(amenities ? { amenities } : {}),
            ...(surfaces ? { surfaces } : {}),
            // A construction parameter, like `units`: the overlay is one texture
            // per floor built at scene time. The PAGE remounts this component
            // when the data source changes, so it never needs swapping in place.
            ...(walkable ? { walkable, showWalkable } : {}),
        });
        apiRef.current = api;
        setReady(true);
        return () => {
            api.dispose();
            apiRef.current = null;
            setReady(false);
        };
    }, []);

    useEffect(() => { if (ready) apiRef.current.setFloor(floor); }, [floor, ready]);
    useEffect(() => { if (ready) apiRef.current.setSelected(selectedId); }, [selectedId, ready]);
    useEffect(() => { if (ready) apiRef.current.setHeatmap(heatmap); }, [heatmap, ready]);
    useEffect(() => { if (ready) apiRef.current.setStackMode(stackMode); }, [stackMode, ready]);
    useEffect(() => { if (ready) apiRef.current.setLabelsVisible(labels); }, [labels, ready]);
    useEffect(() => { if (ready) apiRef.current.setWalkableVisible(showWalkable); }, [showWalkable, ready]);
    useEffect(() => { if (ready) apiRef.current.setTheme(theme); }, [theme, ready]);
    useEffect(() => { if (ready) apiRef.current.setVisibleUnits(visibleUnitIds); }, [visibleUnitIds, ready]);

    useEffect(() => {
        if (!ready) return;
        apiRef.current.setRoute(route);
        if (route) apiRef.current.frameRoute(route);
    }, [route, ready]);

    useImperativeHandle(ref, () => ({
        focusUnit: (id) => apiRef.current && apiRef.current.focusUnit(id),
        resetView: () => apiRef.current && apiRef.current.resetView(),
        screenshot: () => (apiRef.current ? apiRef.current.screenshot() : null),
    }), [ready]);

    return (
        <div className="mm-canvas-host" ref={containerRef} data-scene-theme={theme}>
            {hover && <HoverCard hover={hover} />}
        </div>
    );
});

function HoverCard({ hover }) {
    const { unit, x, y } = hover;
    const perf = unit.performance;
    // Flip the card away from the pointer near the right/bottom edges.
    const flipX = x > window.innerWidth - 300;
    const flipY = y > window.innerHeight - 220;
    const style = {
        left: flipX ? x - 272 : x + 18,
        top: flipY ? y - 200 : y + 18,
    };

    return (
        <div className="mm-hovercard" style={style}>
            <div className="mm-hovercard-head">
                <span className="mm-cat-dot" style={{ background: CATEGORY_COLORS[unit.category] }} />
                <div>
                    <div className="mm-hovercard-name">{unit.name}</div>
                    <div className="mm-hovercard-sub">{unit.code} · {unit.category}</div>
                </div>
            </div>
            <div className="mm-hovercard-grid">
                <div><span>GLA</span><strong>{formatNumber(unit.gla)} m²</strong></div>
                <div><span>Status</span><strong>{unit.status}</strong></div>
                {perf && <div><span>Sales / m²</span><strong>{formatMoney(perf.salesPerSqm)}</strong></div>}
                {perf && <div><span>Footfall 30d</span><strong>{formatNumber(perf.footfall30d)}</strong></div>}
            </div>
            <div className="mm-hovercard-hint">Click for the full unit record</div>
        </div>
    );
}

export default MallCanvas;
